import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import {
  getActiveRequestStatuses,
  getRequestStatusColor,
} from '../placeholders/planningDomain';
import { formatRequestDateEs } from '../placeholders/commercialRequestsUtils';
import { useCommercialCatalogRefresh } from '../../hooks/useCommercialCatalogRefresh';

const TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'request_code' },
  { key: 'Referencia', align: 'left', sortKey: 'reference' },
  { key: 'Productos', align: 'right', sortKey: 'items_count' },
  { key: 'Fecha límite', align: 'left', sortKey: 'needed_by_date' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
  { key: 'OP', align: 'left', sortKey: 'op_code' },
];

const productSummaryLabel = (row) => {
  const count = Number(row.items_count || 0);
  const primary = String(row.primary_product_name || '').trim();
  if (!count) return 'Sin productos';
  if (count === 1) return primary || '1 producto';
  return primary ? `${primary} +${count - 1} más` : `${count} productos`;
};

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'request_code':
      return String(row.request_code || '').toLowerCase();
    case 'reference':
      return String(row.reference || '').toLowerCase();
    case 'items_count':
      return Number(row.items_count || 0);
    case 'needed_by_date':
      return row.needed_by_date ? new Date(row.needed_by_date).getTime() : 0;
    case 'status':
      return String(row.status || '').toLowerCase();
    case 'op_code':
      return String(row.op_code || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const TcoatProductRequestsTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Solicitudes de productos T-COAT',
  addButtonLabel = 'Nueva solicitud',
  attached = false,
  hideChrome = false,
  onAdd,
  onRowClick,
  onBulkDelete,
}) => {
  const ui = useUIColors();
  const catalogTick = useCommercialCatalogRefresh();
  const statusOptions = useMemo(() => getActiveRequestStatuses(), [catalogTick]);
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('needed_by_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete);
  const colCount = showBulkChrome ? TABLE_COLUMNS.length + 1 : TABLE_COLUMNS.length;

  const filteredRows = useMemo(() => {
    let list = rows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.request_code,
          row.origin,
          row.reference,
          row.quotation_code,
          row.sale_code,
          row.op_code,
          row.primary_product_name,
          row.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) list = list.filter((row) => String(row.status || '').trim() === filterStatus);
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)),
    [sortedRows]
  );
  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  const toggleSelectOne = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(key.includes('date') || key === 'items_count' ? 'desc' : 'asc');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterStatus);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || !selectedIds.length) return;
    setBulkPending(true);
    setBulkError('');
    try {
      const result = await onBulkDelete(selectedIds);
      if (result !== false) setSelectedIds([]);
    } catch (err) {
      setBulkError(err?.message || 'No se pudieron eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros`
        : `${rows.length} solicitud${rows.length === 1 ? '' : 'es'} · producción de pintura`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const statusPill = (status) => {
    const color = getRequestStatusColor(status);
    return (
      <span
        style={{
          display: 'inline-flex',
          padding: '2px 8px',
          borderRadius: '999px',
          fontSize: '11px',
          fontWeight: 600,
          background: `${color}22`,
          color,
          border: `1px solid ${color}55`,
        }}
      >
        {status || '—'}
      </span>
    );
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderTop: attached ? 'none' : `1px solid ${border}`,
              borderRadius: attached ? '0 0 12px 12px' : '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, venta, producto, color…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Estado</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando solicitudes T-COAT…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay solicitudes T-COAT registradas.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(Number(row.id));
                return (
                  <tr
                    key={row.id}
                    style={{ borderBottom: `1px solid ${border}`, background: selected ? rowHover : bg }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : bg;
                    }}
                  >
                    {showBulkChrome ? (
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Seleccionar ${row.request_code || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <button
                        type="button"
                        onClick={() => onRowClick?.(row)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 12, color: ui.link || '#2563eb' }}>
                          {row.request_code || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: muted }}>{row.origin || '—'}</div>
                      </button>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                      {row.reference || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, textAlign: 'right', color: text }}>
                      <div style={{ fontWeight: 600 }}>{productSummaryLabel(row)}</div>
                      <div style={{ fontSize: 11, color: muted }}>
                        {Number(row.items_count || 0).toLocaleString('es-CL')} ítem
                        {Number(row.items_count || 0) === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: muted }}>
                      {formatRequestDateEs(row.needed_by_date)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>{statusPill(row.status)}</td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: muted }}>{row.op_code || '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TcoatProductRequestsTable;
